import React, { useEffect, useState } from "react";
import Topbar from "@/components/Topbar";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import AttachmentButton, { AttachmentChip } from "@/components/AttachmentButton";
import { ContactGroupsDialog, ContactPicker } from "@/components/ContactGroups";
import { TemplatePicker } from "@/components/TemplatePicker";
import { useChannelAccounts } from "@/components/AccountTag";
import { TemplateManager } from "@/components/TemplateManager";
import { CampaignExport } from "@/components/CampaignExport";
import { ScheduleDialog, ScheduleActions, fmtRD } from "@/components/CampaignSchedule";

const STATUS_LABEL = { draft: "Borrador", scheduled: "Programada", sending: "Enviando", completed: "Completada", cancelled: "Cancelada" };
import {
  Broadcast, PaperPlaneRight, Trash, Eye, WhatsappLogo, EnvelopeSimple,
  ChatCircle, Users, Star,
} from "@phosphor-icons/react";

const AUDIENCE_LABEL = {
  all: "Todos los clientes",
  vip: "Solo clientes VIP",
  leads_open: "Leads activos",
  custom: "Contactos seleccionados",
  group: "Grupo de contactos",
};

const CHANNEL_ICONS = {
  whatsapp: WhatsappLogo,
  sms: ChatCircle,
  email: EnvelopeSimple,
};

const STATUS_COLORS = {
  draft: "bg-slate-500 text-white",
  scheduled: "bg-blue-500 text-white",
  sending: "bg-amber-500 text-white animate-pulse",
  completed: "bg-emerald-500 text-white",
  cancelled: "bg-red-500 text-white",
};

export default function Campaigns() {
  const accounts = useChannelAccounts();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewCampaign, setPreviewCampaign] = useState(null);
  const [scheduling, setScheduling] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/campaigns");
      setItems(data);
    } catch {
      toast.error("Error cargando campañas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  const hasPending = items.some((c) => c.status === "scheduled" || c.status === "sending");
  useEffect(() => {
    if (!hasPending) return undefined;
    const id = setInterval(() => api.get("/campaigns").then(({ data }) => setItems(data)).catch(() => {}), 30000);
    return () => clearInterval(id);
  }, [hasPending]);

  const openPreview = async (c) => {
    setPreviewCampaign(c);
    setPreview(null);
    try {
      const { data } = await api.post(`/campaigns/${c.id}/preview`);
      setPreview(data);
    } catch {
      toast.error("Error generando preview");
    }
  };

  const sendNow = async (c) => {
    if (!window.confirm(`¿Enviar campaña "${c.name}" a ${c.total_recipients || "?"} destinatarios?`)) return;
    try {
      const { data } = await api.post(`/campaigns/${c.id}/send`);
      toast.success(`Enviando a ${data.total} contactos. El avance se actualiza en la lista.`);
      load();
      setPreviewCampaign(null);
      const poll = setInterval(async () => {
        const { data: d } = await api.get(`/campaigns/${c.id}`).catch(() => ({ data: { status: "completed" } }));
        load();
        if (d.status !== "sending") {
          clearInterval(poll);
          toast.success(`Campaña «${c.name}»: ${d.delivered} enviados · ${d.failed} fallidos`);
        }
      }, 4000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error enviando campaña");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta campaña?")) return;
    try {
      await api.delete(`/campaigns/${id}`);
      toast.success("Campaña eliminada");
      load();
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  return (
    <>
      <Topbar title="Campañas salientes" subtitle="Envía mensajes masivos por WhatsApp, SMS o Email a tu base de clientes" />
      <div className="p-8 flex-1 overflow-y-auto min-h-0">
        <div className="max-w-6xl mx-auto space-y-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2 className="font-heading text-2xl font-bold flex items-center gap-2">
                <Broadcast size={22} className="text-primary" />
                Campañas
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Historial y programación de envíos masivos a clientes
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <CampaignExport />
              <TemplateManager />
              <ContactGroupsDialog />
              <NewCampaignDialog open={open} onOpenChange={setOpen} onCreated={load} />
            </div>
          </div>

          <div className="border border-sky-500/30 bg-sky-500/5 rounded-lg p-4 text-sm" data-testid="campaigns-info">
            <div className="font-semibold text-sky-700 dark:text-sky-400 mb-1">Envío real por WhatsApp</div>
            <div className="text-muted-foreground text-xs leading-relaxed">
              Las campañas de WhatsApp se envían con una <strong>plantilla aprobada por Meta</strong> desde el número que elijas al crear la campaña (por defecto, el número principal). Para probar, crea un grupo pequeño en «Grupos de contactos» y envía primero a ese grupo.
            </div>
          </div>

          {loading && <div className="text-center py-12 text-muted-foreground">Cargando…</div>}
          {!loading && items.length === 0 && (
            <div className="border border-dashed border-border rounded-lg p-12 text-center">
              <Broadcast size={40} className="mx-auto text-muted-foreground/50 mb-3" />
              <div className="font-semibold mb-1">No hay campañas todavía</div>
              <div className="text-sm text-muted-foreground mb-4">Crea tu primera campaña para llegar a leads o clientes VIP</div>
            </div>
          )}

          <div className="grid gap-3">
            {items.map((c) => {
              const CIcon = CHANNEL_ICONS[c.channel] || Broadcast;
              return (
                <div key={c.id} className="border border-border rounded-lg p-4 bg-card hover:bg-accent/40 transition-colors" data-testid={`campaign-item-${c.id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 sm:gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <CIcon size={16} className="text-primary" weight="fill" />
                        <h3 className="font-semibold text-sm">{c.name}</h3>
                        <Badge className={STATUS_COLORS[c.status] || "bg-slate-500 text-white"}>
                          {STATUS_LABEL[c.status] || c.status}
                        </Badge>
                        {c.status === "scheduled" && c.scheduled_at && (
                          <span className="text-xs font-medium text-blue-600" data-testid={`campaign-scheduled-at-${c.id}`}>
                            Sale el {fmtRD(c.scheduled_at)}
                          </span>
                        )}
                      </div>
                      {c.status === "cancelled" && c.cancel_reason && (
                        <div className="text-xs text-red-600 mb-1" data-testid={`campaign-cancel-reason-${c.id}`}>{c.cancel_reason}</div>
                      )}
                      <div className="text-xs text-muted-foreground line-clamp-2 mb-2">{c.body}</div>
                      <div className="flex items-center gap-4 text-[11px] text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Users size={11} /> {AUDIENCE_LABEL[c.audience]} · {c.total_recipients}
                        </span>
                        {c.channel === "whatsapp" && c.channel_account_id && (
                          <span className="flex items-center gap-1" data-testid={`campaign-account-${c.id}`}>
                            <WhatsappLogo size={11} /> {accounts.find((a) => a.id === c.channel_account_id)?.label || "Número eliminado"}
                          </span>
                        )}
                        {(c.status === "completed" || c.status === "sending") && (
                          <>
                            {c.status === "sending" && <span className="text-sky-600" data-testid={`campaign-progress-${c.id}`}>Enviando {c.delivered + c.failed}/{c.total_recipients}…</span>}
                            <span className="text-emerald-600">✓ {c.delivered} enviados</span>
                            {(c.failed > 0 || c.attachments_failed > 0) && <FailuresButton campaign={c} />}
                          </>
                        )}
                        <span>{new Date(c.created_at).toLocaleString()}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap shrink-0">
                      <Button size="sm" variant="outline" onClick={() => openPreview(c)} data-testid={`campaign-preview-${c.id}`}>
                        <Eye size={14} />
                      </Button>
                      <ScheduleActions c={c} onSchedule={setScheduling} onChanged={load} />
                      {(c.status === "draft" || c.status === "scheduled") && (
                        <Button size="sm" onClick={() => sendNow(c)} className="gap-1.5" data-testid={`campaign-send-${c.id}`}>
                          <PaperPlaneRight size={14} /> {c.status === "scheduled" ? "Enviar ahora" : "Enviar"}
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => remove(c.id)} data-testid={`campaign-delete-${c.id}`}>
                        <Trash size={14} className="text-red-500" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {scheduling && <ScheduleDialog campaign={scheduling} onClose={() => setScheduling(null)} onDone={load} />}
      {/* Preview modal */}
      <Dialog open={!!previewCampaign} onOpenChange={(o) => !o && setPreviewCampaign(null)}>
        <DialogContent className="max-w-lg" data-testid="campaign-preview-dialog">
          <DialogHeader>
            <DialogTitle>Preview de campaña</DialogTitle>
            <DialogDescription>
              {previewCampaign?.name} — audiencia: {AUDIENCE_LABEL[previewCampaign?.audience]}
            </DialogDescription>
          </DialogHeader>
          {!preview && <div className="py-6 text-center text-muted-foreground">Cargando muestras…</div>}
          {preview && (
            <div className="space-y-3">
              <div className="text-sm">
                Total destinatarios: <span className="font-bold">{preview.total}</span>
              </div>
              <div className="text-xs text-muted-foreground">Muestra de primeros 5 mensajes renderizados:</div>
              <div className="space-y-2 max-h-72 overflow-auto">
                {preview.samples.map((s, i) => (
                  <div key={i} className="border border-border rounded-md p-3 bg-secondary/50">
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">
                      {s.name} · {s.phone || "sin teléfono"}
                    </div>
                    <div className="text-sm whitespace-pre-wrap">{s.rendered_body}</div>
                  </div>
                ))}
                {preview.samples.length === 0 && (
                  <div className="text-sm text-muted-foreground text-center py-4">Sin destinatarios coincidentes</div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewCampaign(null)}>Cerrar</Button>
            {["draft", "scheduled"].includes(previewCampaign?.status) && (
              <Button onClick={() => sendNow(previewCampaign)} className="gap-1.5">
                <PaperPlaneRight size={14} /> Enviar ahora
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* -------------------- New Campaign Dialog -------------------- */
function FailuresButton({ campaign }) {
  const [rows, setRows] = useState(null);
  const open = async () => setRows((await api.get(`/campaigns/${campaign.id}/failures`).catch(() => ({ data: [] }))).data);
  return (
    <>
      <button className="text-red-500 underline-offset-2 hover:underline" onClick={open} data-testid={`campaign-failures-${campaign.id}`}>
        ✗ {campaign.failed} fallidos{campaign.attachments_failed > 0 ? ` · ${campaign.attachments_failed} sin adjunto` : ""} · ver
      </button>
      <Dialog open={!!rows} onOpenChange={(v) => !v && setRows(null)}>
        <DialogContent className="max-w-2xl" data-testid="campaign-failures-dialog">
          <DialogHeader><DialogTitle>Envíos fallidos · {campaign.name}</DialogTitle></DialogHeader>
          <div className="max-h-96 overflow-y-auto divide-y divide-border text-xs">
            {(rows || []).map((r, i) => (
              <div key={i} className="py-2"><b>{r.name || "Sin nombre"}</b> · <span className="font-mono">{r.phone}</span><div className="text-muted-foreground">{r.error}</div></div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function NewCampaignDialog({ open, onOpenChange, onCreated }) {
  const [name, setName] = useState("");
  const [channel, setChannel] = useState("whatsapp");
  const [audience, setAudience] = useState("group");
  const [body, setBody] = useState("¡Hola {name}! 👋\n\n");
  const [attachments, setAttachments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [template, setTemplate] = useState(null);
  const [groupId, setGroupId] = useState("");
  const [groups, setGroups] = useState([]);
  const [picked, setPicked] = useState([]);
  const waAccounts = useChannelAccounts().filter((a) => a.channel === "whatsapp" && a.active !== false);
  const [accountId, setAccountId] = useState("");
  const defaultAcc = (waAccounts.find((a) => a.is_default) || waAccounts[0])?.id || "";
  useEffect(() => { if (open && !accountId && defaultAcc) setAccountId(defaultAcc); }, [open, defaultAcc, accountId]);
  const pickAccount = (id) => { setAccountId(id); setTemplate(null); };
  useEffect(() => { if (open) api.get("/contact-groups").then(({ data }) => setGroups(data)).catch(() => {}); }, [open]);

  const reset = () => {
    setName(""); setBody("¡Hola {name}! 👋\n\n"); setAttachments([]); setAudience("group"); setChannel("whatsapp");
    setTemplate(null); setGroupId(""); setPicked([]); setAccountId("");
  };

  const submit = async () => {
    const isWa = channel === "whatsapp";
    if (!name.trim() || (!isWa && !body.trim())) return toast.error("Nombre y mensaje son requeridos");
    if (isWa && waAccounts.length && !accountId) return toast.error("Selecciona el número de WhatsApp de salida");
    if (isWa && !template) return toast.error("Selecciona una plantilla aprobada");
    if (audience === "group" && !groupId) return toast.error("Selecciona un grupo");
    if (audience === "custom" && !picked.length) return toast.error("Selecciona al menos un contacto");
    setSaving(true);
    try {
      await api.post("/campaigns", {
        name,
        channel,
        audience,
        body: isWa ? template.body : body,
        template_name: isWa ? template.name : null,
        template_language: isWa ? template.language : "es",
        channel_account_id: isWa ? accountId || null : null,
        group_id: audience === "group" ? groupId : null,
        audience_customer_ids: audience === "custom" ? picked : [],
        attachment_ids: attachments.map((a) => a.id),
      });
      toast.success("Campaña creada como borrador");
      reset();
      onOpenChange(false);
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error creando campaña");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button className="gap-1.5" data-testid="btn-new-campaign">
          <Broadcast size={14} /> Nueva campaña
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="new-campaign-dialog">
        <DialogHeader>
          <DialogTitle>Nueva campaña saliente</DialogTitle>
          <DialogDescription>
            En WhatsApp se envía la plantilla aprobada que elijas ({"{{1}}"} se reemplaza por el primer nombre). En otros canales usa <code>{"{name}"}</code>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">Nombre interno</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej: Promo verano Corolla 2026"
              data-testid="campaign-name"
              className="mt-1"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">Canal</label>
              <Select value={channel} onValueChange={setChannel}>
                <SelectTrigger className="mt-1" data-testid="campaign-channel">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  <SelectItem value="sms">SMS</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">Audiencia</label>
              <Select value={audience} onValueChange={setAudience}>
                <SelectTrigger className="mt-1" data-testid="campaign-audience">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los clientes</SelectItem>
                  <SelectItem value="vip">Solo VIP <Star className="inline" size={11} /></SelectItem>
                  <SelectItem value="leads_open">Leads activos</SelectItem>
                  <SelectItem value="group">Grupo de contactos</SelectItem>
                  <SelectItem value="custom">Elegir contactos</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {audience === "group" && (
            <Select value={groupId} onValueChange={setGroupId}>
              <SelectTrigger data-testid="campaign-group"><SelectValue placeholder="Selecciona un grupo (créalos en «Grupos de contactos»)" /></SelectTrigger>
              <SelectContent>
                {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name} · {g.count} contactos</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {audience === "custom" && <ContactPicker selected={picked} setSelected={setPicked} />}
          {channel === "whatsapp" && waAccounts.length > 0 && (
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">Número de WhatsApp de salida</label>
              <Select value={accountId} onValueChange={pickAccount}>
                <SelectTrigger className="mt-1" data-testid="campaign-account"><SelectValue placeholder="Selecciona un número" /></SelectTrigger>
                <SelectContent>
                  {waAccounts.map((a) => (
                    <SelectItem key={a.id} value={a.id} data-testid={`campaign-account-option-${a.id}`}>
                      {a.label}{a.display_phone_number ? ` · ${a.display_phone_number}` : ""}{a.is_default ? " (principal)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-[10px] text-muted-foreground mt-1">Las respuestas de los clientes llegan al Inbox marcadas con este número.</div>
            </div>
          )}
          {channel === "whatsapp" && (waAccounts.length === 0 || accountId) && (
            <TemplatePicker key={accountId || "default"} value={template} onChange={setTemplate} accountId={accountId || undefined} />
          )}

          {channel !== "whatsapp" && (
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">Mensaje</label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={5}
              placeholder="¡Hola {name}! Tenemos ofertas especiales..."
              data-testid="campaign-body"
              className="mt-1 font-mono text-sm"
            />
            <div className="text-[10px] text-muted-foreground mt-1">
              Tokens disponibles: <code>{"{name}"}</code>, <code>{"{full_name}"}</code>, <code>{"{phone}"}</code>
            </div>
          </div>
          )}

          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2 block">
              Adjuntos (PDF cotizaciones, imágenes)
            </label>
            <div className="flex items-center gap-2 flex-wrap">
              <AttachmentButton onUploaded={(f) => setAttachments((p) => [...p, f])} />
              {attachments.map((f) => (
                <AttachmentChip
                  key={f.id}
                  file={f}
                  onRemove={() => setAttachments((p) => p.filter((x) => x.id !== f.id))}
                />
              ))}
            </div>
            {channel === "whatsapp" && attachments.length > 0 && (
              <div className="text-[11px] text-amber-700 bg-amber-500/10 rounded p-2 mt-2" data-testid="campaign-attachments-warning">
                Regla de Meta: los adjuntos sueltos solo llegan a clientes que escribieron en las últimas 24 h. Para que la imagen llegue a todos,
                usa una plantilla con <b>imagen de encabezado</b>: la primera imagen adjunta se enviará como encabezado.
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={submit} disabled={saving} data-testid="campaign-save">
            {saving ? "Guardando…" : "Crear borrador"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
